import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useStrategyApi from "../api/use-strategy-api";
import { AddKnowledgeSourceResponse } from "../types/strategy";
import { BrandDetails } from "../types/add-brand-types";
import { useNavigation } from "./useNavigation";
import { AppRoute } from "../types/routes";
import { ContentMode } from "../types/creator-studio";
import { isNotEmpty } from "../utils/type-utils";
import useCreatorStudioApi from "../api/use-creator-studio-api";
import useSeoArticleApi from "../api/use-seo-article.api";
import useCurrentPlan from "./use-current-plan";
import { getWebsiteContextDisplay } from "../utils/website-context";

type StrategyProgressKind = "url" | "file" | "delete";

interface StrategyProgressState {
  title: string;
  steps: string[];
  activeIndex: number;
  helperText?: string;
}

const STRATEGY_PROGRESS_COPY: Record<
  StrategyProgressKind,
  { title: string; steps: string[] }
> = {
  url: {
    title: "Adding website knowledge",
    steps: [
      "Preparing source",
      "Extracting website content",
      "Cleaning knowledge",
      "Updating product summary",
      "Refreshing brand workspace",
    ],
  },
  file: {
    title: "Uploading file knowledge",
    steps: [
      "Reading file",
      "Cleaning document",
      "Updating product summary",
      "Refreshing brand workspace",
    ],
  },
  delete: {
    title: "Removing knowledge source",
    steps: [
      "Removing source",
      "Rebuilding product summary",
      "Refreshing brand workspace",
    ],
  },
};

const useStrategy = (
  brandData: BrandDetails,
  refreshBrandDetails: () => void | Promise<void>
) => {
  const { addKnowledgeSource, ingestFile, deleteKnowledgeSource } =
    useStrategyApi();
  const { generateCampaign } = useCreatorStudioApi();
  const { getSavedSeoArticle, saveSeoArticle } = useSeoArticleApi();
  const { push } = useNavigation();
  const {
    plan,
    hasReachedKnowledgeSourceLimit,
    isFree,
  } = useCurrentPlan(brandData.id);
  const [knowledgeAddResponse, setKnowledgeAddResponse] =
    useState<AddKnowledgeSourceResponse | null>(null);
  const [detectedTopics, setDetectedTopics] = useState<string[]>([]);

  const [topics, setTopics] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [progressState, setProgressState] =
    useState<StrategyProgressState | null>(null);

  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info" | "warning";
    isVisible: boolean;
  } | null>(null);

  const [inputValue, setInputValue] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const progressTimersRef = useRef<number[]>([]);

  const clearProgressTimers = useCallback(() => {
    progressTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    progressTimersRef.current = [];
  }, []);

  const startProgress = useCallback(
    (kind: StrategyProgressKind) => {
      clearProgressTimers();
      const copy = STRATEGY_PROGRESS_COPY[kind];
      setProgressState({
        ...copy,
        activeIndex: 0,
      });

      progressTimersRef.current = copy.steps.slice(1).map((_, index) =>
        window.setTimeout(() => {
          setProgressState((current) =>
            current ? { ...current, activeIndex: index + 1 } : current
          );
        }, 2200 * (index + 1))
      );
      progressTimersRef.current.push(
        window.setTimeout(() => {
          setProgressState((current) =>
            current
              ? {
                  ...current,
                  helperText:
                    "Still working. Large or JavaScript-heavy pages can take a little longer.",
                }
              : current
          );
        }, 45000)
      );
      progressTimersRef.current.push(
        window.setTimeout(() => {
          setProgressState((current) =>
            current
              ? {
                  ...current,
                  helperText:
                    "This is taking longer than expected. If it does not finish soon, try a smaller page or upload a PDF/source document.",
                }
              : current
          );
        }, 120000)
      );
    },
    [clearProgressTimers]
  );

  const setProgressToFinalStep = useCallback(() => {
    setProgressState((current) =>
      current
        ? { ...current, activeIndex: Math.max(current.steps.length - 1, 0) }
        : current
    );
  }, []);

  const stopProgress = useCallback(() => {
    clearProgressTimers();
    setProgressState(null);
  }, [clearProgressTimers]);

  useEffect(() => stopProgress, [stopProgress]);

  const addUrlKnowledgeSource = useCallback(
    async (url: string, brandId: string) => {
      if (hasReachedKnowledgeSourceLimit) {
        setToast({
          message: "Free plan includes 1 knowledge source. Upgrade to add more.",
          type: "warning",
          isVisible: true,
        });
        setTimeout(() => setToast(null), 3500);
        return;
      }

      setLoading(true);
      startProgress("url");
      try {
        const response = await addKnowledgeSource(url, brandId, sourceName);

        if (response.status === "success") {
          setKnowledgeAddResponse(response);
          setProgressToFinalStep();
          await refreshBrandDetails();
          setInputValue("");
          setSourceName("");
          setToast({
            message: `Knowledge updated successfully!`,
            type: "success",
            isVisible: true,
          });
        } else {
          setToast({
            message: response.message || "Something went wrong!",
            type: "error",
            isVisible: true,
          });
        }
      } catch {
        setToast({
          message: "Something went wrong!",
          type: "error",
          isVisible: true,
        });
      } finally {
        setLoading(false);
        stopProgress();
        setTimeout(() => {
          setToast(null);
        }, 3500);
      }
    },
    [
      addKnowledgeSource,
      hasReachedKnowledgeSourceLimit,
      refreshBrandDetails,
      sourceName,
      startProgress,
      setProgressToFinalStep,
      stopProgress,
    ]
  );

  const addFileKnowledgeSource = useCallback(
    async (file: File, brandId: string) => {
      if (hasReachedKnowledgeSourceLimit) {
        setToast({
          message: "Free plan includes 1 knowledge source. Upgrade to add more.",
          type: "warning",
          isVisible: true,
        });
        setTimeout(() => setToast(null), 3500);
        return;
      }

      setLoading(true);
      startProgress("file");
      try {
        const response = await ingestFile(file, brandId);

        if (response.status === "success") {
          setKnowledgeAddResponse(response);
          setProgressToFinalStep();
          await refreshBrandDetails();
          setToast({
            message: `File uploaded successfully!`,
            type: "success",
            isVisible: true,
          });
        } else {
          setToast({
            message: response.message || "Failed to upload file!",
            type: "error",
            isVisible: true,
          });
        }
      } catch {
        setToast({
          message: "Failed to upload file!",
          type: "error",
          isVisible: true,
        });
      } finally {
        setLoading(false);
        stopProgress();
        setTimeout(() => {
          setToast(null);
        }, 3500);
      }
    },
    [
      hasReachedKnowledgeSourceLimit,
      ingestFile,
      refreshBrandDetails,
      startProgress,
      setProgressToFinalStep,
      stopProgress,
    ]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        addFileKnowledgeSource(file, brandData.id);
      }
    },
    [addFileKnowledgeSource, brandData.id]
  );

  const removeKnowledgeSource = useCallback(
    async (sourceName: string, brandId: string) => {
      setLoading(true);
      startProgress("delete");
      try {
        const response = await deleteKnowledgeSource(brandId, sourceName);

        if (response.status === "success") {
          setProgressToFinalStep();
          await refreshBrandDetails();
          setToast({
            message: "Source removed successfully!",
            type: "success",
            isVisible: true,
          });
        } else {
          setToast({
            message: response.message || "Failed to remove source.",
            type: "error",
            isVisible: true,
          });
        }
      } catch {
        setToast({
          message: "Failed to remove source.",
          type: "error",
          isVisible: true,
        });
      } finally {
        setLoading(false);
        stopProgress();
        setTimeout(() => setToast(null), 3500);
      }
    },
    [
      deleteKnowledgeSource,
      refreshBrandDetails,
      startProgress,
      setProgressToFinalStep,
      stopProgress,
    ]
  );

  const onSeoItemClick = useCallback(
    async (keyword: string) => {
      if (isFree && !plan?.features.seo) {
        setToast({
          message: "SEO article generation is available on Pro.",
          type: "warning",
          isVisible: true,
        });
        setTimeout(() => setToast(null), 3500);
        return;
      }

      setAiLoading(true);
      const savedArticle = await getSavedSeoArticle(brandData.id, keyword);
      const savedMarkdown = savedArticle?.content_markdown ?? "";
      if (isNotEmpty(savedMarkdown)) {
        push(AppRoute.SEO_PREVIEW.replace(":brandId", brandData.id), {
          markdown: savedMarkdown,
          keyword,
          title: savedArticle?.title,
          description: savedArticle?.description,
          tags: savedArticle?.tags,
          isSaved: true,
        });
        setAiLoading(false);
        return;
      }

      const response = await generateCampaign(
        keyword,
        brandData.id,
        Object.keys(brandData.knowledge_base),
        ContentMode.SEO_ARTICLE
      );

      if (isNotEmpty(response) && isNotEmpty(response?.drafts)) {
        const draft = response.drafts[0];
        const markdownContent =
          typeof draft === "string" ? draft : draft?.body ?? "";
        const metadata =
          typeof draft === "string"
            ? undefined
            : {
                title: draft?.title,
                description: draft?.description,
                tags: draft?.tags,
              };
        await saveSeoArticle(
          brandData.id,
          keyword,
          markdownContent,
          metadata
        );
        push(AppRoute.SEO_PREVIEW.replace(":brandId", brandData.id), {
          markdown: markdownContent,
          keyword,
          title: metadata?.title,
          description: metadata?.description,
          tags: metadata?.tags,
          isSaved: true,
        });
      }

      setAiLoading(false);
    },
    [
      brandData.id,
      brandData.knowledge_base,
      generateCampaign,
      getSavedSeoArticle,
      isFree,
      plan?.features.seo,
      push,
      saveSeoArticle,
    ]
  );

  useEffect(() => {
    if (brandData.latest_insights && brandData.latest_insights !== null) {
      const latestInsights = brandData.latest_insights;
      setDetectedTopics(latestInsights.detected_topics ?? []);
    }
  }, [brandData.latest_insights]);

  const productSummary = useMemo(
    () => (brandData.product_summary || "").trim(),
    [brandData.product_summary]
  );

  const websiteContextDisplay = useMemo(
    () => getWebsiteContextDisplay(brandData),
    [brandData]
  );

  return {
    addUrlKnowledgeSource,
    addFileKnowledgeSource,
    removeKnowledgeSource,
    topics,
    setTopics,
    detectedTopics,
    loading,
    progressState,
    knowledgeAddResponse,
    toast,
    setToast,
    setInputValue,
    inputValue,
    sourceName,
    setSourceName,
    fileInputRef,
    handleFileChange,
    onSeoItemClick,
    aiLoading,
    productSummary,
    websiteContextDisplay,
    plan,
    knowledgeSourceLimitReached: hasReachedKnowledgeSourceLimit,
  };
};

export default useStrategy;
